import { getDataWithQueryString } from '@componentAPIs';
import { RMAXLogger } from '@RMAXLogger';
import {
  EMBARGO_CATEGORIES,
  EMPTY_EMBARGO_CONFIG,
  createEmptyEmbargoCategory,
  getEmbargoCategoryValidationType,
} from './constants';
import { EMBARGO_CONTENT } from './content';
import type {
  EmbargoCategoryConfig,
  EmbargoConfig,
} from './types';
export { parseEmbargoSaveError, resolvePickupSaveFailure } from './utils/parseEmbargoSaveError';
export type {
  ParseEmbargoSaveErrorOptions,
  PickupSaveFailureResolution,
} from './utils/parseEmbargoSaveError';

export type PickupRestrictionRow = {
  terminalCode: string;
  zips: string[];
};

export type EmbargoApiItem = {
  embargoCategory?: string;
  value?: unknown;
};

/** Confirmed PickupRestrictions response body. */
export type PickupRestrictionsApiBody = {
  directPoints?: PickupRestrictionRow[];
  embargoes?: EmbargoApiItem[];
};

export type ParsedPickupRestrictions = {
  restrictions: PickupRestrictionRow[];
  embargoConfig: EmbargoConfig;
};

const toLower = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

const toCategoryConfig = (
  item: EmbargoApiItem | undefined,
  categoryKey: string
): EmbargoCategoryConfig => {
  if (!item) {
    return createEmptyEmbargoCategory();
  }
  const raw = item.value;
  const values = Array.isArray(raw)
    ? raw.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return {
    values,
    validationType: getEmbargoCategoryValidationType(categoryKey),
  };
};

const findEmbargoItem = (
  embargoes: EmbargoApiItem[],
  categoryKey: string
): EmbargoApiItem | undefined =>
  embargoes.find((item) => toLower(item.embargoCategory) === categoryKey);

export const toEmbargoConfig = (
  body: Partial<PickupRestrictionsApiBody> | null | undefined
): EmbargoConfig => {
  const embargoes = Array.isArray(body?.embargoes) ? body.embargoes : [];
  return {
    nonServiceZip: toCategoryConfig(
      findEmbargoItem(embargoes, EMBARGO_CATEGORIES.NON_SERVICE_ZIP),
      EMBARGO_CATEGORIES.NON_SERVICE_ZIP
    ),
    freezableLanesByZip: toCategoryConfig(
      findEmbargoItem(embargoes, EMBARGO_CATEGORIES.FREEZABLE_LANES_BY_ZIP),
      EMBARGO_CATEGORIES.FREEZABLE_LANES_BY_ZIP
    ),
    freezableLanesByTerminal: toCategoryConfig(
      findEmbargoItem(embargoes, EMBARGO_CATEGORIES.FREEZABLE_LANES_BY_TERMINAL),
      EMBARGO_CATEGORIES.FREEZABLE_LANES_BY_TERMINAL
    ),
  };
};

const toPickupRestrictionRow = (entry: unknown): PickupRestrictionRow | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as { terminalCode?: unknown; zips?: unknown };
  const terminalCode =
    typeof record.terminalCode === 'string' ? record.terminalCode.trim() : '';
  const zips = Array.isArray(record.zips)
    ? record.zips.filter((zip): zip is string => typeof zip === 'string')
    : [];
  if (!terminalCode) {
    return null;
  }
  return { terminalCode, zips };
};

const filterValidPickupRestrictionRows = (
  rows: unknown[]
): PickupRestrictionRow[] =>
  rows
    .map(toPickupRestrictionRow)
    .filter((row): row is PickupRestrictionRow => Boolean(row));

/**
 * Pickup restriction rows used by Pickup Management (terminal code + allowed zips).
 */
export const normalizePickupRestrictionsData = (
  value: unknown
): PickupRestrictionRow[] => {
  if (Array.isArray(value)) {
    return filterValidPickupRestrictionRows(value);
  }
  if (
    value &&
    typeof value === 'object' &&
    Array.isArray((value as PickupRestrictionsApiBody).directPoints)
  ) {
    return filterValidPickupRestrictionRows(
      (value as PickupRestrictionsApiBody).directPoints as unknown[]
    );
  }
  return [];
};

export const parsePickupRestrictionsResponse = (
  body: Partial<PickupRestrictionsApiBody> | null | undefined
): ParsedPickupRestrictions => ({
  restrictions: normalizePickupRestrictionsData(body),
  embargoConfig: toEmbargoConfig(body),
});

export type PickupRestrictionsCache = {
  data?: PickupRestrictionRow[];
  embargoConfig?: EmbargoConfig;
  terminalCode?: string;
  embargoPickupDate?: string;
};

export type PickupRestrictionsCacheCheck = {
  cached: PickupRestrictionsCache | null | undefined;
  terminalCode: string;
  pickupDate?: string | null;
};

export const isPickupRestrictionsCacheValid = ({
  cached,
  terminalCode,
  pickupDate,
}: PickupRestrictionsCacheCheck): boolean =>
  Boolean(cached?.embargoConfig) &&
  Array.isArray(cached?.data) &&
  cached?.terminalCode === terminalCode &&
  (cached?.embargoPickupDate ?? undefined) === (pickupDate ?? undefined);

export const fetchPickupRestrictionsWithEmbargo = async (
  terminalCode: string,
  pickupDate?: string | null
): Promise<ParsedPickupRestrictions> => {
  const query = pickupDate
    ? `terminalCode=${terminalCode}&currentDate=${pickupDate}`
    : `terminalCode=${terminalCode}`;
  try {
    const response = (await getDataWithQueryString(
      'PickupRestrictions',
      query
    )) as { data?: Partial<PickupRestrictionsApiBody> };
    return parsePickupRestrictionsResponse(response?.data);
  } catch (error) {
    RMAXLogger.error(EMBARGO_CONTENT.logging.loadFailed, {
      terminalCode,
      pickupDate,
      error,
    });
    return {
      restrictions: [],
      embargoConfig: EMPTY_EMBARGO_CONFIG,
    };
  }
};
